import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { asyncScheduler, Observable, of, timer } from 'rxjs';
import { catchError, mergeMap, observeOn, tap, toArray } from 'rxjs/operators';
import { NzMessageService } from 'ng-zorro-antd/message';
import { LoginService } from '../http/login.service';
import { ClaimsAppSettings } from '../../../ViewModules/Claims/claims-configs';
import { MultiLingualService } from '../services';
import { AppMetadataHandler } from '../../configs';
import { ThreadsService } from '../utilities/threads.service';
import { environment } from 'environments/environment';
import { LoginInfoInStorage, UserInfoService } from '../http/user-info.service';
import { ApiRequestService } from '../http/api-request.service';

export interface Credentials {
  username: string;
  token: string;
  type?: string;
}

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
}

const CREDENTIALS_KEY = 'credentials';

/**
 * Provides a base for authentication workflow.
 * The Credentials interface as well as login/logout methods should be replaced with proper implementation.
 */
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private _credentials: Credentials | null;
  private _errorMsg: string;
  public loadingFromToken: boolean;

  constructor(
    private msg: NzMessageService,
    private appSettings?: ClaimsAppSettings,
    private router?: Router,
    public loginService?: LoginService,
    private multiLingualService?: MultiLingualService,
    private appMetadataHandler?: AppMetadataHandler,
    private threadService?: ThreadsService,
    private apiRequestService?: ApiRequestService,
    private userInfoService?: UserInfoService) {
    this.restoreCredentials();
  }

  /**
   * Restores user credentials from session or local storage
   */
  private restoreCredentials(): void {
    const savedCredentials = sessionStorage.getItem(CREDENTIALS_KEY) || localStorage.getItem(CREDENTIALS_KEY);
    if (savedCredentials) {
      this._credentials = JSON.parse(savedCredentials);
    }
  }

  /**
   * Basic authentication method (for testing)
   */
  login(context: LoginContext): Observable<Credentials> {
    const data = {
      username: context.username,
      token: '123456',
      type: null
    };
    this.setCredentials(data, context.remember);
    return of(data);
  }

  /**
   * Fetches authentication token from server
   */
  fetchToken(context: LoginContext, landingRoute): void {
    const messageId = this.msg.loading('Authenticating').messageId;
    
    this.loginService.getToken(context.username, context.password)
      .subscribe({
        next: (resp) => {
          if (resp.success) {
            this._loginSuccessHandler(resp, context, landingRoute, messageId);
          } else {
            console.log('Authentication Failed');
            if (!environment.production) {
              console.error('Check Allowed Roles in AppMetaDataHandler');
            }
            this.msg.warning("Incorrect Username/Password");
            this.loadingFromToken = false;
          }
        },
        error: (errResponse) => {
          this.handleAuthenticationError(errResponse);
        }
      });
  }
  
  /**
   * Maps HTTP error status codes to user-friendly messages
   */
  private handleAuthenticationError(errResponse: any): void {
    switch (errResponse.status) {
      case 401:
        this._errorMsg = 'Username or password is incorrect';
        break;
      case 404:
        this._errorMsg = 'Service not found';
        break;
      case 408:
        this._errorMsg = 'Request Timedout';
        break;
      case 500:
        this._errorMsg = 'Internal Server Error';
        break;
      default:
        this._errorMsg = 'Server Error';
    }
    this.msg.error(this._errorMsg);
    this.loadingFromToken = false;
  }
 
loginWithToken(user: any, responseCode: string, landingRoute: any) {
    var messageId = this.msg.loading('Authenticating').messageId;
    //Authentication
    if (responseCode == 'SUCCESS') {
      let resp = {};
      resp['user'] = (user);
      resp['status'] = true;
      //Authorization
      this.apiRequestService.token = user.token;
      let loginInfoReturn: LoginInfoInStorage = {
        "success": true,
        "message": "Successful login",
        "landingPage": landingRoute,
        "user": {
          "userId": null,
          "firstName": null,
          "email": null,
          "displayName": null,
          "token": null,
          "oneTimePasswordFl": false,
          "statusCd": null,
          "typeCd": null,
          "userRoleDOList": [],
          "userObjectResp": null
        }
      };
      //Fill in the success object that we want to send back to login page
      loginInfoReturn.success = true;
      loginInfoReturn.message = 'Successful login'
      loginInfoReturn.landingPage = landingRoute
      loginInfoReturn.user.userId = user.userId
      loginInfoReturn.user = { ...user }
      loginInfoReturn.user.email = loginInfoReturn.user['emailId'] //
      loginInfoReturn.user.displayName = loginInfoReturn.user['firstName'] //

      if (loginInfoReturn.user.baseCompanyCd) {
        this.loginService.fetchCompanyByNum(loginInfoReturn.user.baseCompanyCd);
      }

      //Pentest Caution: Might be a Pentest Issue
      loginInfoReturn.user.userObjectResp = user
      loginInfoReturn.user.token = user.token

      // store userId and jwt token in session storage to keep user logged in between page refreshes
      this.userInfoService.storeUserInfo(JSON.stringify(user));
      this.apiRequestService.getAPIMaster().subscribe((res) => {
        this._loginSuccessHandler(resp, { username: user.userName, password: null, remember: true }, landingRoute, messageId);
        this.router.navigate([landingRoute]);
      });
    }
  }


  /**
   * Handles successful login
   */
  _loginSuccessHandler(resp: any, context?: LoginContext, landingRoute?: any, messageId?: string) {
    if (!this.currentRolePrivilege(this.appSettings._allowedRoles, resp.user.typeCd)) {
      this.loadingFromToken = false;
      this.msg.error("Authorization Error");
      return;
    }
    
    // Set user credentials
    const data = {
      username: resp.user.userId,
      token: resp.user.token,
      type: resp.user.typeCd
    };
    
    this.setCredentials(data, context.remember);
    this.multiLingualService.init();

    // Run post-login initialization processes
    this.threadService.POSTLOGINTHREAD().subscribe(
      {
        next: () => {
          this.threadService._successThreadLog('Post login thread completed');
           // Handle navigation based on language loading
          if (!this.appMetadataHandler.featureFlags.waitForLabelsAfterLogin) {
            landingRoute = this.extractEntryPointAndNavigateToLanding(resp, landingRoute);
            this.router.navigate([landingRoute]);
          } else {
            this.multiLingualService.currentLanguageLoaded.subscribe(lang => {
              if (lang === this.appMetadataHandler.defaultLanguage) {
                if (messageId) {
                  this.msg.remove(messageId);
                }
                landingRoute = this.extractEntryPointAndNavigateToLanding(resp, landingRoute);
                this.router.navigate([landingRoute]);
              }
            });
          }
        },
        error: () => {
          this.threadService._errorThreadLog('There is some problem in post login thread');
        }
      }
    );
  }

  /**
   * Finds and navigates to the correct landing route based on user role
   */
  private extractEntryPointAndNavigateToLanding(resp: any, landingRoute: any): string {
    resp.user.userRoleDOList.forEach(userRole => {
      const foundRoleSpecificLanding = this.appMetadataHandler.getEntryPoint(userRole.roleId);
      if (foundRoleSpecificLanding) {
        landingRoute = foundRoleSpecificLanding;
      }
    });
    return landingRoute;
  }

  /**
   * Bypasses normal login for testing purposes
   */
  _bypassLoginSuccessHandler(context?: LoginContext, landingRoute?: any) {
    const data = {
      username: "test user",
      token: "test 1234",
      type: "test"
    };
    
    this.setCredentials(data, context.remember);
    
    this.threadService.POSTLOGINTHREAD().pipe(
      toArray(),
      tap(() => this.threadService._successThreadLog('Post login thread completed')),
      catchError(() => {
        this.threadService._errorThreadLog('There is some problem in post login thread');
        return of([]);
      })
    ).subscribe();
    
    this.router.navigate([landingRoute]);
    return landingRoute;
  }

  /**
   * Logs out the user and clears credentials
   */
  logout(): Observable<boolean> {
    this.setCredentials();
    return of(true);
  }

  /**
   * Returns whether user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.credentials;
  }

  /**
   * Returns authenticated user type
   */
  getUserType(): string {
    return this._credentials?.type;
  }

  /**
   * Checks if user has required privileges
   */
  checkUserPrivilege(allowedRoles: Array<any>): boolean {
    const userType = this.getUserType();
    return allowedRoles.some(role => userType === role);
  }

  /**
   * Checks if a specific role has privileges
   */
  currentRolePrivilege(allowedRoles: Array<any>, currentRole: string): boolean {
    return allowedRoles.some(role => currentRole === role);
  }

  /**
   * Gets the user credentials
   */
  get credentials(): Credentials | null {
    return this._credentials;
  }

  /**
   * Sets user credentials with optional persistence
   */
  private setCredentials(credentials?: Credentials, remember?: boolean) {
    this._credentials = credentials || null;

    if (credentials) {
      const storage = remember ? localStorage : sessionStorage;
      storage.setItem(CREDENTIALS_KEY, JSON.stringify(credentials));
    } else {
      sessionStorage.removeItem(CREDENTIALS_KEY);
      localStorage.removeItem(CREDENTIALS_KEY);
    }
  }
}
